import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useLocation, useNavigate } from "react-router-dom";

import CloseIcon from "@mui/icons-material/Close";
import InfoSharpIcon from "@mui/icons-material/InfoSharp";
import WarningIcon from "@mui/icons-material/Warning";
import * as Mui from "@mui/material";
import { DateTimePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import dayjs from "dayjs";
import { GlobalContext } from "../../../../util/state/GlobalContextProvider";
import { ContextOperation } from "../../context/OperationContext";

import useOperationTranslation from "../../hooks/useOperationTranslation";
import { IVehicleExitState } from "../../page/VehicleExitPage";
import AutoComplete from "../common/AutoComplete";
import VehicleAvailabilityService, {
  EnumStatusReasons,
} from "../vehicle-availability/service/VehicleAvailabilityService";
import VehicleExitService from "./service/VehicleExitService";

import useSnackbar from "../../hooks/useSnackbar";
import OperationTypes from "../../types";
import { formatTypeString } from "../vehicle-entrance/utils";

export interface FormInputs {
  driver: string | null;
  endDate: string | null;
  startDate: string | null;
}

export interface Driver {
  label: string;
  value: string;
}

export interface LocationState {
  from: string;
  fromType: string;
  startDate: string;
}

const VehicleExitStatus = () => {
  const { globalData } = useContext(GlobalContext);
  const { setLoading } = useContext(ContextOperation);
  const navigate = useNavigate();
  const location = useLocation();
  const snackbar = useSnackbar();
  const t = useOperationTranslation();

  const form = useForm<any>({
    defaultValues: {
      locationStart: (location.state as LocationState)?.from,
      startDate: dayjs(),
      endDate: null,
    },
  });

  const [availabilityStatus, setAvailabilityStatus] =
    useState<null | OperationTypes.VehicleAvailability.Status>(null);
  const [locations, setLocations] = useState([]);
  const [garageLocations, setGarageLocations] = useState([]);
  const [fromLocations, setfromLocations] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [allDrivers, setAllDrivers] = useState([]);
  const [open, setOpen] = useState<any>(false);
  const [warning, setWarning] = useState(false);
  const [warningDialog, setWarningDialog] = useState(false);
  const [dialogData, setDialogData] = useState<any>({});
  const [damages, setDamages] = useState([]);
  const vehicleData: IVehicleExitState = location.state as IVehicleExitState;

  const startLocation = useMemo(() => {
    return fromLocations.find((loc: any) => loc.bpCode === (location.state as LocationState).from && loc.type === (location.state as LocationState).fromType) || {} as any
  }, [fromLocations]);

  // when you go back from VehicleExitConfirm
  useEffect(() => {

    // drivers
    if ((location.state as { drivers: any }).drivers) {
      setDrivers((location.state as { drivers: any }).drivers);
    }

    // damage forms
    if ((location.state as { damage: any }).damage) {
      form.setValue("damage", (location.state as { damage: any }).damage?.id);
    }
    
    // to
    if ((location.state as { to: any }).to){
      form.setValue("locationEnd", (location.state as { to: any })?.to?.value);
    }
    
    // Arrival date
    if ((location.state as { endDate: any }).endDate){
      form.setValue("endDate", (location.state as { endDate: any })?.endDate);
    }
  }, [location]);

  const getData = useCallback(() => {
    if (vehicleData === null || typeof vehicleData === "undefined") {
      navigate("/operation/transfer/vehiclesExit");
    } else {
      VehicleExitService.get
        .allDrivers(vehicleData.tenant)
        .then((response: any) => setAllDrivers(response));

      VehicleAvailabilityService.get
        .unavailabilityByVehicle(vehicleData.tenant, vehicleData.id)
        .then((response) => {
          setAvailabilityStatus(response);
          return response;
        });

      VehicleExitService.get
        .destinationLocations(vehicleData.tenant)
        .then((response) => setGarageLocations(response));

      VehicleExitService.get
        .destinationLocationsFrom(vehicleData.tenant)
        .then((response) => setfromLocations(response));

      VehicleExitService.get
        .damageForm(vehicleData.tenant, vehicleData.plate)
        .then((response) => {
          setDamages(response);
        });

      const mobilityStores = globalData.user?.mobilityStores || [];
      const fromLocationIsAssoiciatedToUser = mobilityStores.filter(
        ({ sapId }: any) => sapId === (location.state as LocationState).from
      )[0];

      if (!fromLocationIsAssoiciatedToUser && mobilityStores[0]?.id !== 0) {
        setWarning(true);
        setWarningDialog(true);
      }
    }
  }, []);

  useEffect(() => {
    getData();
  }, [getData]);

  const onSubmit = (data: any) => {
    setLoading(true);

    const endLocation: any = garageLocations.filter(
      ({ value }) => value === form.watch("locationEnd")
    )[0];

    const goToNextStep = (distance: any) =>
      navigate(`/operation/transfer/vehiclesExit/confirm`, {
        state: {
          ...(location.state as LocationState),
          tenant: vehicleData.tenant,
          drivers: drivers,
          locations: locations,
          endDate: dayjs(form.watch("endDate")).toISOString(),
          chipLabel: availabilityStatus?.vehicleStatus,
          from: startLocation.bpCode,
          fromLocation: startLocation,
          to: endLocation,
          distance: distance,
          damage: damages.find(
            (damage: any) => damage.id === form.watch("damage")
          ),
        },
      });

    VehicleExitService.get
      .locationDistance(
        vehicleData.tenant,
        (location.state as LocationState).from,
        endLocation?.bpCode
      )
      .then(
        (response) => {
          setLoading(false);
          goToNextStep({ km: response });
        },
        ({ response }) => {
          setLoading(false);
          if (response?.status === 406) {
            goToNextStep({ status: 406 });
          } else {
            snackbar.show("error", t("vehicleExit.confirm.genericError"));
          }
        }
      );
  };

  const chipLabel = `${availabilityStatus?.vehicleStatus}`;

  const handleClose = () => {
    setOpen(false);
  };

  const checkDrivers = () => {
    setOpen(true);
    setDialogData({
      title: "",
      bodyTitle: "",
      body: t("vehicleExit.status.dialogBody"),
    });
  };

  return (
    vehicleData && (
      <>
        <Mui.Box paddingBottom="16px">
          <Mui.Grid container spacing={{ md: 2 }} p={{ md: 2 }}>
            <Mui.Grid item md={6} xs={12}>
              <Mui.Card>
                <Mui.CardContent>
                  <Mui.Stack gap={2} alignItems="center" mb={2}>
                    <Mui.Skeleton
                      variant="rectangular"
                      width="150px"
                      height="150px"
                    />
                    <Mui.Typography
                      variant="h4"
                      mb="10px"
                      textAlign={{ md: "left", xs: "center" }}
                    >
                      {vehicleData.modelType}
                    </Mui.Typography>
                    <Mui.Chip
                      label={chipLabel}
                      color={
                        availabilityStatus?.vehicleStatus ===
                        EnumStatusReasons.AVAILABLE
                          ? "info"
                          : "warning"
                      }
                    />
                  </Mui.Stack>
                  <Mui.Box sx={{ display: "flex", gap: "10px" }} pt="20px">
                    <Mui.Typography variant="body1">
                      {t(
                        "vehicleAvailability.status.currentState.labels.plate"
                      )}
                    </Mui.Typography>
                    <Mui.Typography variant="h5">
                      {vehicleData.plate}
                    </Mui.Typography>
                  </Mui.Box>
                </Mui.CardContent>
              </Mui.Card>
            </Mui.Grid>

            <Mui.Grid item md={6} xs={12}>
              <Mui.Card>
                <Mui.CardContent>
                  <form {...form} onSubmit={form.handleSubmit(onSubmit)}>
                    <Mui.Grid container spacing={0}>
                      <Mui.Typography variant="h5">
                        {t("vehicleExit.status.driverLabel")}
                      </Mui.Typography>

                      <Mui.Autocomplete
                        multiple
                        sx={{ width: "100% !important" }}
                        id="drivers"
                        value={drivers}
                        onChange={(event, newValue: any) => {
                          setDrivers(newValue);
                        }}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        options={allDrivers}
                        getOptionLabel={(option) => option.label}
                        renderTags={(tagValue, getTagProps) =>
                          tagValue.map((option, index) => (
                            <Mui.Chip
                              label={option.label}
                              {...getTagProps({ index })}
                            />
                          ))
                        }
                        style={{ width: 500 }}
                        renderInput={(params) => (
                          <Mui.TextField
                            {...params}
                            error={
                              Array.isArray(drivers) &&
                              drivers.length === 0 &&
                              form.formState.isSubmitting
                            }
                            placeholder={
                              drivers.length === 0
                                ? t("vehicleExit.status.multipleSelectLabel")
                                : ""
                            }
                          />
                        )}
                      />

                      <Mui.Grid item xs={12} mt={5}>
                        <Mui.Typography variant="h5">
                          {t("vehicleExit.status.damageForms")}
                        </Mui.Typography>

                        <AutoComplete
                          fullWidth
                          options={damages}
                          label={t("vehicleExit.status.damageForms")}
                          {...form.register("damage")}
                          value={form.watch("damage")}
                          onChange={(damage) => {
                            form.setValue("damage", damage);
                          }}
                        />
                      </Mui.Grid>

                      <Mui.Grid item xs={12} mt={5}>
                        <Mui.Typography variant="h5">
                          {t("vehicleExit.status.from")}
                        </Mui.Typography>

                        <AutoComplete
                          disabled
                          fullWidth
                          options={locations}
                          label={`${startLocation?.locationEntityDesc} (${formatTypeString((location.state as LocationState).fromType, t)})`}
                        />
                      </Mui.Grid>

                      <Mui.Grid item xs={12} mt={5}>
                        <Mui.Typography variant="h5">
                          {t("vehicleExit.status.to")}
                        </Mui.Typography>
                        <AutoComplete
                          fullWidth
                          options={garageLocations.filter(
                            ({ type }) => type === "SHOP_REPAIR"
                          )}
                          label={t("vehicleExit.status.locationEnd")}
                          {...form.register("locationEnd", {
                            required: t("vehicleExit.status.toErrorMessage"),
                          })}
                          value={form.watch("locationEnd")}
                          onChange={(locationEnd) => {
                            form.setValue("locationEnd", locationEnd);
                          }}
                          error={form.formState.errors?.locationEnd?.message?.toString()}
                        />
                      </Mui.Grid>

                      <Mui.Grid item xs={12} mt={2}>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                          <DateTimePicker
                            minDateTime={dayjs()}
                            renderInput={(props) => (
                              <Mui.TextField
                                {...props}
                                fullWidth
                                error={!!form.formState.errors.endDate}
                                helperText={form.formState.errors.endDate?.message?.toString()}
                                onReset={(event) =>
                                  form.setValue("endDate", null)
                                }
                              />
                            )}
                            inputFormat={"DD/MM/YYYY HH:mm"}
                            label={t("vehicleExit.status.expectedArrivalDate")}
                            ampm={false}
                            value={form.watch("endDate")}
                            {...form.register("endDate", {
                              required: t("vehicleExit.status.toErrorMessage"),
                              validate: (value) => {
                                const dateSelected = dayjs(value);
                                if (value && !dateSelected.isValid()) {
                                  return t(
                                    "vehicleAvailability.status.newBlock.inputs.endDateInvalid"
                                  );
                                }
                                if (
                                  dateSelected.isBefore(
                                    dayjs(form.watch("startDate"))
                                  )
                                ) {
                                  return t(
                                    "vehicleAvailability.status.newBlock.inputs.endDateBeforeStart"
                                  );
                                }
                              },
                            })}
                            onChange={(newDateValue) => {
                              form.setValue("endDate", newDateValue);
                            }}
                          />
                        </LocalizationProvider>
                      </Mui.Grid>

                      <Mui.Grid item xs={12} mt={2}>
                        <Mui.Button
                          fullWidth
                          variant="contained"
                          disabled={warning}
                          onClick={
                            drivers.length === 0
                              ? checkDrivers
                              : form.handleSubmit(onSubmit)
                          }
                          style={{
                            ...(!warning && {
                              color: "white",
                              backgroundColor: "#d12c93",
                            }),
                          }}
                        >
                          {t("vehicleExit.status.confirmVehicleExit")}
                        </Mui.Button>
                      </Mui.Grid>
                    </Mui.Grid>

                    <Mui.Dialog
                      open={open}
                      onClose={handleClose}
                      aria-labelledby="alert-dialog-title"
                      aria-describedby="alert-dialog-description"
                    >
                      <Mui.DialogTitle>
                        <Mui.Box sx={{ display: "flex" }}>
                          <InfoSharpIcon
                            sx={{
                              color: "#C8017D",
                              marginRight: "17px",
                            }}
                          />

                          <Mui.Typography fontWeight={"bold"} color={"primary"}>
                            {dialogData.title}
                          </Mui.Typography>
                        </Mui.Box>

                        <Mui.IconButton
                          aria-label="close"
                          onClick={handleClose}
                          sx={{
                            position: "absolute",
                            right: 8,
                            top: 8,
                          }}
                        >
                          <CloseIcon />
                        </Mui.IconButton>
                      </Mui.DialogTitle>

                      <Mui.DialogContent>
                        {dialogData.bodyTitle && (
                          <Mui.DialogContentText
                            id="alert-dialog-description"
                            style={{ marginBottom: 20 }}
                          >
                            {dialogData.bodyTitle}
                          </Mui.DialogContentText>
                        )}

                        <Mui.DialogContentText id="alert-dialog-description">
                          {dialogData.body}
                        </Mui.DialogContentText>
                      </Mui.DialogContent>
                      <Mui.DialogActions>
                        <Mui.Button
                          type="submit"
                          variant="contained"
                          onClick={form.handleSubmit(onSubmit, () =>
                            setOpen(false)
                          )}
                        >
                          {t("vehicleExit.status.confirmVehicle")}
                        </Mui.Button>

                        <Mui.Button
                          onClick={handleClose}
                          style={{ color: "#d12c93", backgroundColor: "white" }}
                        >
                          {t("vehicleExit.status.cancelVehicle")}
                        </Mui.Button>
                      </Mui.DialogActions>
                    </Mui.Dialog>

                    <Mui.Dialog open={warningDialog}>
                      <Mui.DialogTitle>
                        <WarningIcon
                          sx={{ color: "orange", fontSize: "20px" }}
                        />
                      </Mui.DialogTitle>

                      <Mui.DialogContent>
                        {t(
                          "vehicleExit.status.fromLocationNotAssociated"
                        ).replace("username", globalData.user.username)}
                      </Mui.DialogContent>

                      <Mui.DialogActions>
                        <Mui.Button
                          variant="contained"
                          onClick={() => setWarningDialog(false)}
                        >
                          OK
                        </Mui.Button>
                      </Mui.DialogActions>
                    </Mui.Dialog>
                  </form>
                </Mui.CardContent>
              </Mui.Card>
            </Mui.Grid>
          </Mui.Grid>
        </Mui.Box>
      </>
    )
  );
};
export default VehicleExitStatus;
